import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Pill } from "lucide-react";

import { apiError } from "@/shared/api/client";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useOperatesModule } from "@/shared/hooks/useOperatesModule";
import { clinicFeaturesApi, clinicFeaturesQueryKeys } from "@/shared/api/clinicFeatures";
import { drugDbApi, medicinesApi } from "@/shared/api/medicines";
import { inventoryApi, inventoryQueryKeys } from "@/shared/api/inventory";
import { prescriptionTemplatesApi } from "@/shared/api/prescriptionTemplates";
import type { PrescriptionTemplateResponse } from "@/shared/types/prescriptionTemplate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Wait this long after the last keystroke before searching (#19172). */
export const SEARCH_DEBOUNCE_MS = 250;
/** A single character matches most of the catalogue; search from two. */
export const MIN_SEARCH_CHARS = 2;

/** What a picked medicine hands back — the caller turns it into a prescription line. */
export interface PickedMedicine {
  id: string;
  name: string;
  unit: string;
  strength: string | null;
}

/**
 * One search box for both toa mẫu and individual medicines (TD-7b/1, #18806).
 *
 * <p><b>Owns its four lookups and the query text; owns no prescription.</b> It hands back a picked
 * medicine or a whole template and never touches the encounter's rx list — that list is shared with
 * the signing flow, so one owner keeps it. Four props, none of them a setter.
 *
 * <p>Extracted first, and on its own, because the Kê đơn section is ~400 lines over 20 coupled
 * helpers: moving it in one step is where a silent behaviour change hides. The characterisation
 * tests written in #18806 cover exactly this surface — catalog search, template apply, and the
 * ADR-0027 stock gate — so the move is verifiable rather than hopeful.
 */
export function MedicinePicker({
  onPickMedicine,
  onApplyTemplate,
  onOpenTemplateModal,
  onError,
}: {
  onPickMedicine: (m: PickedMedicine) => void;
  onApplyTemplate: (template: PrescriptionTemplateResponse) => void;
  onOpenTemplateModal: () => void;
  /** Reported to the workspace's shared error line, where the doctor already looks. */
  onError: (message: string | null) => void;
}) {
  const [medQ, setMedQ] = useState("");
  // #19172 (L8): the queries key on the debounced term, not on every keystroke. Typing
  // "Paracetamol" used to send 11 pairs of /medicines + /prescription-templates requests (and, on an
  // empty catalogue, 11 national drug DB calls). One character matches too much to be useful, so the
  // search waits for two.
  const term = useDebounce(medQ.trim(), SEARCH_DEBOUNCE_MS);
  const searchable = term.length >= MIN_SEARCH_CHARS;

  // visibleOnly: true — thuốc đã ẩn không được hiện ở màn khám (khớp với RxSearchPicker).
  // placeholderData keeps the previous list on screen while the next term loads, so it does not
  // blink empty between keystrokes.
  const medResults = useQuery({
    queryKey: ["enc-med-pick", term],
    queryFn: () =>
      medicinesApi.list({ q: term, visibleOnly: true }, { size: 8 }).then((r) => r.content),
    enabled: searchable,
    placeholderData: keepPreviousData,
  });
  // Fallback to the national drug DB when the tenant catalog returns nothing. A placeholder is the
  // previous term's answer, so it says nothing about whether THIS term is empty.
  const tenantEmpty =
    searchable &&
    medResults.isFetched &&
    !medResults.isPlaceholderData &&
    (medResults.data ?? []).length === 0;
  const drugDbResults = useQuery({
    queryKey: ["enc-drugdb-pick", term],
    queryFn: () => drugDbApi.search(term, 8),
    enabled: tenantEmpty,
  });
  // One search box feeds both groups: prescription templates on top, medicines below. Previously
  // the doctor had to decide which of two boxes to type into before the search that would answer it.
  const tplResults = useQuery({
    queryKey: ["enc-tpl-pick", term],
    queryFn: () =>
      prescriptionTemplatesApi
        .list({ q: term, visibleOnly: true }, { page: 0, size: 6, sort: "name" })
        .then((r) => r.content),
    enabled: searchable,
    placeholderData: keepPreviousData,
  });

  // Tồn kho là vùng Tồn kho, bán riêng (ADR-0053): gói có Đơn thuốc mà không có Tồn kho thì lượt
  // gọi này nhận 402 ở mỗi lần mở ô. Vẽ huy hiệu là gọi API, nên chờ `loaded`.
  const inventory = useOperatesModule("INVENTORY");
  const stockRows = useQuery({
    queryKey: inventoryQueryKeys.stock(),
    queryFn: () => inventoryApi.stock(),
    enabled: inventory.loaded && inventory.operates,
    staleTime: 30_000,
  });
  const onHandByMedicine = new Map<string, number>();
  for (const row of stockRows.data ?? []) {
    onHandByMedicine.set(
      row.medicineId,
      (onHandByMedicine.get(row.medicineId) ?? 0) + (row.quantityOnHand ?? 0)
    );
  }
  const stockKnown = !stockRows.isLoading && !stockRows.isError;
  // Cấu hình Kê toa & Thuốc (ADR-0027). Tồn kho ("Còn N" / "Hết hàng") chỉ hiện khi phòng khám bật
  // "Cho phép bán thuốc" — cơ sở chỉ kê toa lâm sàng không cần thấy số lượng/tồn kho.
  const clinicFeatures = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60_000,
  });
  const showStock = clinicFeatures.data?.allowSelling ?? false;

  /**
   * Hands the medicine up, then clears the box — the search text has done its job and leaving it
   * would keep the results open over the line just added. Clearing lives here, not in the caller:
   * the query text is this component's state and nothing outside it should be able to reset it.
   */
  function pick(m: PickedMedicine) {
    onPickMedicine(m);
    setMedQ("");
  }

  /** Enter applies an exact toa mẫu code straight away — the fastest path for doctors who know it. */
  async function loadTemplateByCode(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;
    onError(null);
    try {
      const res = await prescriptionTemplatesApi.list(
        { q: trimmed, visibleOnly: true },
        { page: 0, size: 5, sort: "name" }
      );
      const match =
        res.content.find((t) => t.code.toLowerCase() === trimmed.toLowerCase()) ?? res.content[0];
      if (!match) {
        onError(`Không tìm thấy toa mẫu "${trimmed}".`);
        return;
      }
      const full = await prescriptionTemplatesApi.get(match.id);
      onApplyTemplate(full);
      setMedQ("");
    } catch (e) {
      onError(apiError(e).detail);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          className="flex-1"
          value={medQ}
          onChange={(ev) => setMedQ(ev.target.value)}
          onKeyDown={(ev) => {
            // Enter still applies an exact template code straight
            // away — the fastest path for doctors who know it.
            if (ev.key === "Enter") {
              ev.preventDefault();
              loadTemplateByCode(medQ);
            }
          }}
          placeholder="Tìm toa mẫu hoặc thuốc — mã, tên, hoạt chất…"
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => onOpenTemplateModal()}
        >
          <Pill className="mr-1.5 h-4 w-4" /> Chọn toa mẫu
        </Button>
      </div>
      {medQ.trim().length >= MIN_SEARCH_CHARS && (
        <div className="mt-2 overflow-hidden rounded-md border">
          {/* ── Toa thuốc mẫu ── */}
          {(tplResults.data ?? []).length > 0 && (
            <div className="bg-muted px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground">
              Toa thuốc mẫu
            </div>
          )}
          {(tplResults.data ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              className="block w-full border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
              onClick={async () => {
                const full = await prescriptionTemplatesApi.get(t.id);
                onApplyTemplate(full);
                setMedQ("");
              }}
            >
              <strong>{t.name}</strong>
              <span className="text-muted-foreground"> · {t.code}</span>
              {t.diagnosis ? ` · ${t.diagnosis}` : ""}
              {` · ${t.itemCount} thuốc`}
            </button>
          ))}

          {/* ── Thuốc ── */}
          {(medResults.data ?? []).length > 0 && (
            <div className="bg-muted px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground">
              Thuốc
            </div>
          )}
          {(medResults.data ?? []).map((m) => {
            const qty =
              showStock && stockKnown
                ? (onHandByMedicine.get(m.id) ?? 0)
                : null;
            const out = qty !== null && qty <= 0;
            return (
              <button
                key={m.id}
                type="button"
                className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                onClick={() =>
                  pick({
                    id: m.id,
                    name: m.name,
                    unit: m.unit,
                    strength: m.strength,
                  })
                }
              >
                <span className="flex-1">
                  <strong>{m.name}</strong>
                  {m.strength ? ` · ${m.strength}` : ""} · {m.unit}
                </span>
                {qty !== null && (
                  <Badge variant={out ? "destructive" : "secondary"}>
                    {out
                      ? "Hết hàng"
                      : `Còn ${qty.toLocaleString("vi-VN")} ${m.unit}`}
                  </Badge>
                )}
              </button>
            );
          })}

          {/* National drug DB fallback when the tenant catalog is empty */}
          {tenantEmpty &&
            (drugDbResults.data ?? []).map((d) => (
              <button
                key={d.nationalCode}
                type="button"
                className="block w-full border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                onClick={() =>
                  pick({
                    id: `drugdb:${d.nationalCode}`,
                    name: d.name,
                    unit: d.unit ?? "Viên",
                    strength: d.strength,
                  })
                }
              >
                <strong>{d.name}</strong>
                {d.strength ? ` · ${d.strength}` : ""}
                {d.unit ? ` · ${d.unit}` : ""}
                <span className="text-muted-foreground"> · CSDL Quốc gia</span>
              </button>
            ))}
          {tenantEmpty &&
            drugDbResults.isFetched &&
            (drugDbResults.data ?? []).length === 0 &&
            (tplResults.data ?? []).length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Không tìm thấy toa mẫu hay thuốc nào.
              </div>
            )}
        </div>
      )}
    </>
  );
}
